import { Session, SubscriptionEnum, User } from "../lib/types";
import { useCallback, useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { ReloadIcon } from "@radix-ui/react-icons";
import { ColumnDef, ColumnFiltersState } from "@tanstack/react-table"
import { DataTable } from "./ui/data-table";
import { ArrowUpDown } from "lucide-react"
import { getSession } from "../lib/api";
import { ScrollArea } from "./ui/scroll-area";
import { getSubscriptionDisplayName } from "../lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";


export const sessionTableColumns: ColumnDef<Session>[] = [
  {
    accessorKey: "session_id",
    header: "ID",
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Creation date
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      )
    },
    sortingFn: 'alphanumericCaseSensitive',
    cell: ({row}) => {
      return <span>{new Date(row.original.created_at + 'Z' ).toLocaleString(navigator.language)}</span>
    }
  },
  {
    accessorKey: "session_type",
    header: "Session type",
    cell: ({row}) => {
      return <span>{getSubscriptionDisplayName(row.original.session_type)}</span>
    }
  }
];

export const sessionTableFilterColumns: ColumnFiltersState = [
  {
    id: "session_id",
    value: "",
  },
];

export default function Sessions({sessions, showSessionsDetails, selectedUser, refreshUserSessions, sessionType, setSessionType}: {sessions: Session[], showSessionsDetails: (session: Session) => void, selectedUser: User | null, refreshUserSessions: () => void, sessionType: SubscriptionEnum, setSessionType: (sessionType: SubscriptionEnum) => void}) {
  const [sessionId, setSessionId] = useState<string | null>(null);  
  const [session, setSession] = useState<Session[]>(sessions);  

  useEffect(() => {
    setSession(sessions);
  }, [sessions]);

  const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if(sessionId){
      getSession(sessionId).then((data) => {
          setSession([data]);
      });
    } else {
      refreshUserSessions();
    }
  }, [sessionId, refreshUserSessions]);

  return (
      <Card className="h-full flex flex-col">
        <CardHeader>
          <CardTitle>
            {selectedUser && selectedUser.user_name + "'s sessions"}
          
            { selectedUser && <Button onClick={() => refreshUserSessions()} size={'icon'} variant={'outline'} className="ml-2 border-0">
              <ReloadIcon className="w-4 h-3 stroke-4" />
            </Button>}
        </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 overflow-hidden">
            <div className="flex">
              <form onSubmit={handleSubmit} className="flex flex-col justify-between gap-2 mb-2">
                <div className="flex flex-row gap-1 items-center">
                  <label className="text-sm font-medium w-36">Session type</label>
                  <Select defaultValue={sessionType} onValueChange={(value) => setSessionType(value as SubscriptionEnum)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a session type" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(SubscriptionEnum).map((subscription) => (
                        <SelectItem value={subscription}>{getSubscriptionDisplayName(subscription)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input type="text" placeholder="Session id" value={sessionId ?? ''} onChange={(e) => { e.preventDefault(); setSessionId(e.target.value) }} className="border border-gray-300 rounded-md p-2 w-60" />
                <Button type="submit" className="w-24 bg-gray-500 text-white">Search</Button>
              </form>
            </div>
            <h1 className="text-lg font-semibold">Results</h1>
              <ScrollArea type='scroll' className="overflow-y-auto h-full">
                <DataTable columns={sessionTableColumns} data={session} onRowClick={showSessionsDetails} filterColumns={sessionTableFilterColumns} /> 
              </ScrollArea> 
        </CardContent>
      </Card>
  )
}